import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Select, DatePicker, Table, Space, Tag, Typography,
  Spin, Button, Tabs, Progress, Tooltip, Badge, Divider, Alert, Segmented,
} from 'antd';
import {
  FileTextOutlined, ShoppingCartOutlined, ClockCircleOutlined, DollarOutlined,
  AlertOutlined, FieldTimeOutlined, CheckCircleOutlined, CloseCircleOutlined,
  BarChartOutlined, RiseOutlined, WarningOutlined, SafetyOutlined,
  InboxOutlined, ExclamationCircleOutlined, FundOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, AreaChart, Area,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import { getDashboard, getLookups, listCancellations } from '../../api';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { estimateGapLabel } from '../../utils/priceSource';
import type { CancellationQueueResult } from '../../api';
import CancellationQueue from '../../components/CancellationQueue';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS: Record<string, string> = {
  Draft: '#d9d9d9', Submitted: '#1890ff', 'Partially Approved': '#faad14',
  Approved: '#52c41a', Rejected: '#ff4d4f', Ordered: '#722ed1',
  'Partially Ordered': '#2f54eb', Open: '#1890ff', 'Partially Received': '#13c2c2',
  Received: '#52c41a', Closed: '#8c8c8c', Cancelled: '#ff7875',
  'Pending Cancellation': '#faad14', Reviewed: '#597ef7',
};
const RAG_COLORS: Record<string, string> = { Green: '#52c41a', Amber: '#faad14', Red: '#ff4d4f', 'No Data': '#d9d9d9' };
const URGENCY_COLORS: Record<string, string> = { Low: 'default', Normal: 'blue', High: 'orange', Critical: 'red', Urgent: 'orange' };
const fmt = (val: number) => val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (val: number) => `${(val * 100).toFixed(1)}%`;
const fmt0 = (val: number) => Math.round(val || 0).toLocaleString('en-US');
/** Net of cancelled (2026-09-22): the tile shows the net value; this line names the stored total and the cancelled part. */
const NetLine: React.FC<{ gross?: number; cancelled?: number; word: 'ordered' | 'requested' }> = ({ gross, cancelled, word }) =>
  Number(cancelled || 0) > 0.005 ? (
    <div style={{ fontSize: 11.5, color: '#8a94a6', marginTop: 2, lineHeight: 1.3 }}>
      {fmt0(gross || 0)} {word} · <span style={{ color: '#b42323' }}>{fmt0(cancelled || 0)} cancelled</span>
    </div>
  ) : null;

const Dashboard: React.FC = () => {
  const { user, isAdmin, isProcurement, isApprover } = useAuth();
  const navigate = useNavigate();
  const isCompactLayout = useCompactLayout(); // phones + small tablets (2026-10-19)
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [departments, setDepartments] = useState<Array<{ id: number; name: string }>>([]);
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  // Waiting-for-you panel (2026-09-16): Approvals / Cancellations switch in one card.
  const [waitingSide, setWaitingSide] = useState<'approvals' | 'cancellations'>('approvals');
  const [cancelSummary, setCancelSummary] = useState<CancellationQueueResult['summary'] | null>(null);
  const [cancelRefresh, setCancelRefresh] = useState(0);
  const loadCancelSummary = useCallback(async () => {
    try { const r = await listCancellations({ limit: 1 }); setCancelSummary(r.summary); } catch { /* panel shows without the count */ }
  }, []);
  useEffect(() => { loadCancelSummary(); }, [loadCancelSummary, cancelRefresh]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (departmentIds.length) params.departmentIds = departmentIds.join(',');
      if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');
      setData(await getDashboard(params));
    } catch (err) { console.error('Failed to load dashboard', err); }
    finally { setLoading(false); }
  }, [departmentIds, dateRange]);

  useEffect(() => { getLookups().then(l => setDepartments(l.departments)).catch(console.error); }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const showDeptFilter = isAdmin || isProcurement;
  const showPendingApprovals = isAdmin || isApprover;

  const prByStatus = useMemo(() => {
    if (!data?.mrsByStatus) return [];
    return Object.entries(data.mrsByStatus).filter(([, v]: any) => (v?.count || v) > 0)
      .map(([status, v]: any) => ({ status, count: typeof v === 'object' ? v.count : v, value: typeof v === 'object' ? v.value : 0 }));
  }, [data?.mrsByStatus]);

  const poByStatus = useMemo(() => {
    if (!data?.posByStatus) return [];
    return Object.entries(data.posByStatus).filter(([, v]: any) => (v?.count || v) > 0)
      .map(([status, v]: any) => ({ status, count: typeof v === 'object' ? v.count : v, value: typeof v === 'object' ? v.value : 0 }));
  }, [data?.posByStatus]);

  const lineKPIs = data?.lineKPIs || {};
  const grn = data?.grnStats || {};
  const exec = data?.poExecutiveMetrics || {};

  // Health score: simple composite 0-100
  const healthScore = useMemo(() => {
    if (!data) return 0;
    let score = 100;
    if ((data.pendingApprovalCount || 0) > 5) score -= 10;
    if ((data.overdueCount || 0) > 0) score -= Math.min(20, data.overdueCount * 5);
    if ((data.pendingCancellations?.length || 0) > 0) score -= 5;
    if ((lineKPIs.linesApprovedWaitingPO || 0) > 10) score -= 10;
    if ((grn.totalRejected || 0) > 0) score -= Math.min(15, Math.round(grn.totalRejected / (grn.totalDelivered || 1) * 100));
    if ((grn.returnsPending || 0) > 0) score -= 5;
    return Math.max(0, Math.min(100, score));
  }, [data, lineKPIs, grn]);

  const healthColor = healthScore >= 80 ? '#52c41a' : healthScore >= 60 ? '#faad14' : '#ff4d4f';

  if (!data && !loading) return <div style={{ textAlign: 'center', padding: 48 }}><Text>No data available</Text></div>;

  // The "N MRs · open full list" line of the Waiting-for-you card: top right on laptops, under the switch on phones.
  const waitingExtra = waitingSide === 'approvals'
    ? <Text type="secondary" style={{ fontSize: 12 }}>{data?.pendingApprovalMRs?.length || 0} MR{(data?.pendingApprovalMRs?.length || 0) === 1 ? '' : 's'} · <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/mrs?status=Submitted')}>open full list</Button></Text>
    : <Text type="secondary" style={{ fontSize: 12 }}>{cancelSummary ? `${fmt(cancelSummary.value)} EGP waiting · oldest ${cancelSummary.oldestHours >= 24 ? `${Math.floor(cancelSummary.oldestHours / 24)} d` : `${cancelSummary.oldestHours} h`} · ` : ''}<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/cancellations')}>open full list</Button></Text>;

  return (
    <div>
      {/* Header with filters */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Procurement Dashboard</Title></Col>
        <Col>
          <Space>
            {showDeptFilter && (
              <Select
                mode="multiple"
                maxTagCount="responsive"
                optionFilterProp="label"
                placeholder="All Departments"
                allowClear
                style={{ minWidth: 200 }}
                onChange={(vals) => setDepartmentIds(vals as number[])}
                value={departmentIds}
                options={departments.map(d => ({ label: d.name, value: d.id }))}
              />
            )}
            <RangePicker onChange={(dates) => setDateRange(dates as any)} value={dateRange} />
          </Space>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Tabs defaultActiveKey="ceo" items={[

          // ============================== TAB 1: CEO OVERVIEW ==============================
          {
            key: 'ceo',
            label: <span><ExclamationCircleOutlined /> CEO Overview</span>,
            children: (
              <>
                {/* Health Score + Top Actions */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Progress type="dashboard" percent={healthScore} strokeColor={healthColor} size={120}
                        format={(p) => <span style={{ fontSize: 24, fontWeight: 700, color: healthColor }}>{p}</span>} />
                      <div style={{ marginTop: 8 }}><Text strong>Procurement Health</Text></div>
                    </Card>
                  </Col>
                  <Col xs={24} md={18}>
                    <Row gutter={[12, 12]}>
                      <Col xs={12} sm={6}>
                        <Card size="small" hoverable onClick={() => setWaitingSide('approvals')} style={{ cursor: 'pointer', borderColor: waitingSide === 'approvals' ? '#faad14' : undefined }}><Statistic title="Pending Approvals" value={data?.pendingApprovalCount || 0}
                          prefix={<ClockCircleOutlined />} valueStyle={{ color: (data?.pendingApprovalCount || 0) > 0 ? '#faad14' : '#52c41a' }} /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" hoverable onClick={() => setWaitingSide('cancellations')} style={{ cursor: 'pointer', borderColor: waitingSide === 'cancellations' ? '#fa8c16' : undefined }}><Statistic title="Pending Cancellations" value={cancelSummary?.count ?? (data?.pendingCancellations?.length || 0)}
                          prefix={<WarningOutlined />} valueStyle={{ color: (cancelSummary?.count ?? data?.pendingCancellations?.length ?? 0) > 0 ? '#fa8c16' : undefined }} /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Overdue POs" value={data?.overdueCount || 0}
                          prefix={<AlertOutlined />} valueStyle={{ color: '#ff4d4f' }} /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Waiting for PO" value={lineKPIs.linesApprovedWaitingPO || 0}
                          prefix={<FieldTimeOutlined />} valueStyle={{ color: (lineKPIs.linesApprovedWaitingPO || 0) > 0 ? '#faad14' : undefined }} /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Total MR Value" value={data?.totalMRValue || 0} prefix={<DollarOutlined />} precision={0} /><NetLine gross={data?.totalMRValueGross} cancelled={data?.cancelledMRValue} word="requested" /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Total PO Value" value={data?.totalPOValue || 0} precision={0} valueStyle={{ color: '#52c41a' }} /><NetLine gross={data?.totalPOValueGross} cancelled={data?.cancelledPOValue} word="ordered" /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Quality Rate" value={grn.totalGRNs > 0 ? pct(grn.qualityRate || 1) : '-'}
                          prefix={<CheckCircleOutlined />} valueStyle={{ color: (grn.qualityRate || 1) >= 0.95 ? '#52c41a' : '#faad14' }} /></Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small"><Statistic title="Avg MR to PO" value={data?.avgMRtoPODays ?? '-'}
                          prefix={<FieldTimeOutlined />} suffix={data?.avgMRtoPODays ? 'd' : ''} valueStyle={{ color: (data?.avgMRtoPODays || 0) > 5 ? '#faad14' : '#52c41a' }} /></Card>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Waiting for you: Approvals / Cancellations in one card (2026-09-16) */}
                {showPendingApprovals && (
                  <Card
                    size="small"
                    style={{ marginBottom: 16 }}
                    styles={{ header: { borderBottom: `2px solid ${waitingSide === 'approvals' ? '#faad14' : '#fa8c16'}`, ...(isCompactLayout ? { padding: '8px 12px' } : {}) }, title: isCompactLayout ? { whiteSpace: 'normal', overflow: 'visible' } : undefined }}
                    title={
                      <Space size={12} wrap>
                        <span>Waiting for you</span>
                        <Segmented
                          size="small"
                          value={waitingSide}
                          onChange={(v) => setWaitingSide(v as any)}
                          options={[
                            { value: 'approvals', label: <span>Approvals <Badge count={data?.pendingApprovalMRs?.length || 0} color="#faad14" size="small" style={{ marginInlineStart: 4 }} /></span> },
                            { value: 'cancellations', label: <span>Cancellations <Badge count={cancelSummary?.count || 0} size="small" style={{ marginInlineStart: 4 }} /></span> },
                          ]}
                        />
                        {isCompactLayout && <div style={{ width: '100%', fontWeight: 400 }}>{waitingExtra}</div>}
                      </Space>
                    }
                    extra={isCompactLayout ? undefined : waitingExtra}
                  >
                    {waitingSide === 'approvals' ? (
                      <ResponsiveTable
                        card={{ header: ['mrId', 'urgency'], footer: ['go'] }}
                        columns={[
                          { title: 'MR ID', dataIndex: 'mrId', render: (v: string, r: any) => <Button type="link" size="small" onClick={() => navigate(`/mrs/${r.id}`)} style={{ padding: 0 }}>{v}</Button> },
                          { title: 'Department', dataIndex: 'departmentName' },
                          { title: 'Requested By', dataIndex: 'requestedBy' },
                          { title: 'Requested', dataIndex: 'total', align: 'right' as const, render: (v: number) => fmt(v) },
                          // 2026-10-19 (Ahmed): history estimate = qty x usual price over lines with purchase history; never a price.
                          { title: 'History estimate', key: 'est', align: 'right' as const, render: (_: any, r: any) => {
                            if (r.historyEstimate == null || !r.estimateCovered) return <Text type="secondary" style={{ fontSize: 12 }}>no purchase history</Text>;
                            const req = Number(r.total || 0); const est = Number(r.historyEstimate);
                            const gap = estimateGapLabel(est, req);
                            return (
                              <span>
                                ~{Math.round(est).toLocaleString('en-US')}
                                {gap && <Tag color="orange" style={{ marginLeft: 6, marginRight: 0 }}>{gap}</Tag>}
                                <div style={{ fontSize: 11, color: '#8a94a6' }}>for {r.estimateCovered} of {r.estimateLines} lines</div>
                              </span>
                            );
                          } },
                          { title: 'Urgency', dataIndex: 'urgency', render: (v: string) => <Tag color={URGENCY_COLORS[v] || 'default'}>{v}</Tag> },
                          { title: 'Waiting', dataIndex: 'daysWaiting', align: 'right' as const,
                            render: (v: number) => <span style={{ color: v > 3 ? '#ff4d4f' : v > 1 ? '#faad14' : undefined, fontWeight: v > 3 ? 600 : undefined }}>{v}d</span> },
                          { title: '', key: 'go', width: 110, render: (_: any, r: any) => <Button size="small" type="primary" onClick={() => navigate(`/mrs/${r.id}`)}>Decide lines</Button> },
                        ]}
                        dataSource={data?.pendingApprovalMRs || []} rowKey="id" pagination={false} size="small"
                        locale={{ emptyText: 'Nothing waiting for approval' }} />
                    ) : (
                      <CancellationQueue key={cancelRefresh} mode="pending" limit={10} onChanged={() => { setCancelRefresh((n) => n + 1); fetchData(); }} />
                    )}
                  </Card>
                )}

                {/* Overdue POs */}
                {(data?.overduePOs?.length || 0) > 0 && (
                  <Card title={<><AlertOutlined style={{ color: '#ff4d4f' }} /> Overdue Purchase Orders</>} size="small"
                    style={{ marginBottom: 16 }} extra={<Tag color="error">{data.overduePOs.length} overdue</Tag>}>
                    <ResponsiveTable
                      columns={[
                        { title: 'PO ID', dataIndex: 'poId', render: (v: string, r: any) => <Button type="link" size="small" onClick={() => navigate(`/pos/${r.id}`)} style={{ padding: 0 }}>{v}</Button> },
                        { title: 'Vendor', dataIndex: 'vendorName' }, { title: 'Department', dataIndex: 'departmentName' },
                        { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
                        { title: 'Open Value', dataIndex: 'openValue', align: 'right' as const, render: (v: number) => fmt(v) },
                        { title: 'Age', dataIndex: 'ageDays', align: 'right' as const, render: (v: number | null) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{v ?? '-'}d</span> },
                      ]}
                      dataSource={data.overduePOs} rowKey="id" pagination={false} size="small" card={{ header: ['poId', 'status'] }} />
                  </Card>
                )}

                {/* Quick Charts */}
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card title="PR by Status" size="small">
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={prByStatus} cx="50%" cy="50%" outerRadius={80} dataKey="count" nameKey="status"
                            label={({ status, count }) => `${status}: ${count}`} labelLine>
                            {prByStatus.map((e, i) => <Cell key={`pr-${i}`} fill={STATUS_COLORS[e.status] || '#8884d8'} />)}
                          </Pie>
                          <RechartsTooltip formatter={(v: any, _: any, p: any) => [`${v} (${fmt(p.payload.value)})`, p.payload.status]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="PO by Status" size="small">
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={poByStatus} cx="50%" cy="50%" outerRadius={80} dataKey="count" nameKey="status"
                            label={({ status, count }) => `${status}: ${count}`} labelLine>
                            {poByStatus.map((e, i) => <Cell key={`po-${i}`} fill={STATUS_COLORS[e.status] || '#8884d8'} />)}
                          </Pie>
                          <RechartsTooltip formatter={(v: any, _: any, p: any) => [`${v} (${fmt(p.payload.value)})`, p.payload.status]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================== TAB 2: SPENDING & BUDGET ==============================
          {
            key: 'spending',
            label: <span><DollarOutlined /> Spending</span>,
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Total MR Value" value={data?.totalMRValue || 0} precision={0} prefix={<FileTextOutlined />} /><NetLine gross={data?.totalMRValueGross} cancelled={data?.cancelledMRValue} word="requested" /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Total PO Value" value={data?.totalPOValue || 0} precision={0} valueStyle={{ color: '#722ed1' }} /><NetLine gross={data?.totalPOValueGross} cancelled={data?.cancelledPOValue} word="ordered" /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Open PO Value" value={data?.totalOpenPOValue || 0} precision={0} valueStyle={{ color: '#faad14' }} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Avg MR Value" value={data?.avgMRValue || 0} precision={0} /></Card></Col>
                </Row>

                {/* Department Spending */}
                <Card title="Department Spending" size="small" style={{ marginBottom: 16 }}>
                  <ResponsiveTable
                    columns={[
                      { title: 'Department', dataIndex: 'departmentName', key: 'dept' },
                      { title: 'Estimated (PR)', dataIndex: 'prValue', align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'Estimated (PO)', dataIndex: 'poValue', align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'Actual', dataIndex: 'actualValue', align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'Variance', key: 'var', align: 'right' as const, render: (_: any, r: any) => {
                        const est = r.poValue || 0; const act = r.actualValue || 0; const diff = act - est;
                        if (est === 0 && act === 0) return '-';
                        const p = est > 0 ? ((diff / est) * 100).toFixed(1) : '0.0';
                        return <span style={{ color: diff > 0 ? '#cf1322' : diff < 0 ? '#3f8600' : undefined }}>{diff > 0 ? '+' : ''}{fmt(diff)} ({p}%)</span>;
                      }},
                    ]}
                    dataSource={data?.departmentSpending || []} rowKey="departmentName" pagination={false} size="small"
                    card={{ total: (() => { const pd = data?.departmentSpending || []; const tPR = pd.reduce((s: number, r: any) => s + r.prValue, 0); const tPO = pd.reduce((s: number, r: any) => s + r.poValue, 0); const tAct = pd.reduce((s: number, r: any) => s + (r.actualValue || 0), 0); const tDiff = tAct - tPO; const tP = tPO > 0 ? ((tDiff / tPO) * 100).toFixed(1) : '0.0'; return pd.length ? <span><strong>Total</strong> · MR {fmt(tPR)} · PO {fmt(tPO)} · actual {fmt(tAct)} · <strong style={{ color: tDiff > 0 ? '#cf1322' : tDiff < 0 ? '#3f8600' : undefined }}>{tDiff > 0 ? '+' : ''}{fmt(tDiff)} ({tP}%)</strong></span> : undefined; })() }}
                    summary={(pd) => {
                      const tPR = pd.reduce((s: number, r: any) => s + r.prValue, 0);
                      const tPO = pd.reduce((s: number, r: any) => s + r.poValue, 0);
                      const tAct = pd.reduce((s: number, r: any) => s + (r.actualValue || 0), 0);
                      const tDiff = tAct - tPO; const tP = tPO > 0 ? ((tDiff / tPO) * 100).toFixed(1) : '0.0';
                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right"><strong>{fmt(tPR)}</strong></Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right"><strong>{fmt(tPO)}</strong></Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right"><strong>{fmt(tAct)}</strong></Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right"><strong style={{ color: tDiff > 0 ? '#cf1322' : tDiff < 0 ? '#3f8600' : undefined }}>{tDiff > 0 ? '+' : ''}{fmt(tDiff)} ({tP}%)</strong></Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </Card>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={12}>
                    <Card title="Category Breakdown" size="small">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={(data?.categoryBreakdown || []).filter((c: any) => c.totalValue > 0)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11 }} />
                          <RechartsTooltip formatter={(v: any) => fmt(v)} />
                          <Bar dataKey="totalValue" fill="#722ed1" name="Value" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="Monthly PR Value Trend" size="small">
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={data?.monthlyTrend || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <RechartsTooltip formatter={(v: any) => fmt(v)} />
                          <Area type="monotone" dataKey="value" stroke="#1890ff" fill="#e6f4ff" strokeWidth={2} name="PR Value" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================== TAB 3: OPERATIONS ==============================
          {
            key: 'operations',
            label: <span><BarChartOutlined /> Operations</span>,
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Total MRs" value={data?.totalMRs || 0} prefix={<FileTextOutlined />} /></Card></Col>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Total POs" value={data?.totalPOs || 0} prefix={<ShoppingCartOutlined />} /></Card></Col>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Open MRs" value={data?.openMRs || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Ordered POs" value={data?.orderedPOs || 0} valueStyle={{ color: '#722ed1' }} /></Card></Col>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Received POs" value={data?.receivedPOs || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col xs={12} sm={8} md={4}><Card size="small"><Statistic title="Closed POs" value={data?.closedPOs || 0} valueStyle={{ color: '#8c8c8c' }} /></Card></Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={8}>
                    <Card title="Line-Level KPIs" size="small">
                      <Row gutter={[8, 12]}>
                        <Col span={12}><Statistic title="Lines Ordered" value={lineKPIs.linesOrdered || 0} valueStyle={{ fontSize: 20 }} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} /></Col>
                        <Col span={12}><Statistic title="Fully Received" value={lineKPIs.linesFullyReceived || 0} valueStyle={{ fontSize: 20 }} prefix={<SafetyOutlined style={{ color: '#1890ff' }} />} /></Col>
                        <Col span={12}><Statistic title="Rejected" value={lineKPIs.linesRejected || 0} valueStyle={{ fontSize: 20, color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} /></Col>
                        <Col span={12}><Statistic title="Waiting PO" value={lineKPIs.linesApprovedWaitingPO || 0} valueStyle={{ fontSize: 20, color: '#faad14' }} prefix={<ClockCircleOutlined />} /></Col>
                      </Row>
                      <Divider style={{ margin: '12px 0' }} />
                      <Row gutter={[8, 8]}>
                        <Col span={12}><Text type="secondary">Avg MR&#8594;PO:</Text> <Text strong>{lineKPIs.avgMRtoPODays || 0}d</Text></Col>
                        <Col span={12}><Text type="secondary">Max MR&#8594;PO:</Text> <Text strong>{lineKPIs.maxMRtoPODays || 0}d</Text></Col>
                        <Col span={12}><Text type="secondary">&le;3d conv:</Text> <Text strong style={{ color: '#52c41a' }}>{lineKPIs.linesConvertedLte3Days || 0}</Text></Col>
                        <Col span={12}><Text type="secondary">&gt;7d conv:</Text> <Text strong style={{ color: '#ff4d4f' }}>{lineKPIs.linesConvertedGt7Days || 0}</Text></Col>
                      </Row>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card title="PO Aging" size="small">
                      {(data?.poAging || []).map((b: any) => (
                        <div key={b.bucket} style={{ marginBottom: 10 }}>
                          <Row justify="space-between"><Text>{b.bucket}</Text><Text strong>{b.poCount} POs ({fmt(b.poValue)})</Text></Row>
                          <Progress percent={data?.totalPOs > 0 ? Math.round((b.poCount / data.totalPOs) * 100) : 0}
                            strokeColor={b.bucket === '90+ Days' ? '#ff4d4f' : b.bucket === '61-90 Days' ? '#faad14' : '#52c41a'} size="small" />
                        </div>
                      ))}
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card title="PO Executive Metrics" size="small">
                      <Row gutter={[8, 12]}>
                        <Col span={12}><Statistic title="Received Value" value={exec.receivedValue || 0} precision={0} valueStyle={{ fontSize: 18, color: '#52c41a' }} /></Col>
                        <Col span={12}><Statistic title="Avg PO Size" value={exec.avgPOSize || 0} precision={0} valueStyle={{ fontSize: 18 }} /></Col>
                        <Col span={12}><Statistic title="Partial Received" value={exec.partiallyReceivedPOs || 0} valueStyle={{ fontSize: 18 }} /></Col>
                        <Col span={12}><Statistic title="Vendor Risk" value={pct(exec.vendorRiskPct || 0)} valueStyle={{ fontSize: 18, color: (exec.vendorRiskPct || 0) > 0.2 ? '#ff4d4f' : '#52c41a' }} /></Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>

                {/* Recent POs */}
                <Card title="Recent Purchase Orders" size="small">
                  <ResponsiveTable
                    columns={[
                      { title: 'PO ID', dataIndex: 'poId', render: (v: string, r: any) => <Button type="link" size="small" onClick={() => navigate(`/pos/${r.id}`)} style={{ padding: 0 }}>{v}</Button> },
                      { title: 'Date', dataIndex: 'poDate', render: (v: string) => dayjs(v).format('DD MMM YY') },
                      { title: 'Vendor', dataIndex: 'vendorName' },
                      { title: 'Total', dataIndex: 'poTotal', align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
                    ]}
                    dataSource={data?.recentPOs || []} rowKey="id" pagination={false} size="small" card={{ header: ['poId', 'status'] }} />
                </Card>
              </>
            ),
          },

          // ============================== TAB 4: RECEIVING & QUALITY ==============================
          {
            key: 'quality',
            label: <span><InboxOutlined /> Receiving</span>,
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Total GRNs" value={grn.totalGRNs || 0} prefix={<InboxOutlined />} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Quality Rate" value={grn.totalGRNs > 0 ? pct(grn.qualityRate || 1) : '-'}
                    valueStyle={{ color: (grn.qualityRate || 1) >= 0.95 ? '#52c41a' : (grn.qualityRate || 1) >= 0.8 ? '#faad14' : '#ff4d4f' }}
                    prefix={<CheckCircleOutlined />} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Items Rejected" value={grn.totalRejected || 0}
                    valueStyle={{ color: (grn.totalRejected || 0) > 0 ? '#ff4d4f' : undefined }} prefix={<CloseCircleOutlined />} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending Returns" value={grn.returnsPending || 0}
                    valueStyle={{ color: (grn.returnsPending || 0) > 0 ? '#faad14' : undefined }} prefix={<WarningOutlined />} /></Card></Col>
                </Row>

                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={8} sm={6}><Card size="small"><Statistic title="Total Delivered" value={grn.totalDelivered || 0} /></Card></Col>
                  <Col xs={8} sm={6}><Card size="small"><Statistic title="Total Accepted" value={grn.totalAccepted || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col xs={8} sm={6}><Card size="small"><Statistic title="Returns Shipped" value={grn.returnsShipped || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                  <Col xs={24} sm={6}><Card size="small"><Statistic title="Returns Credited" value={grn.returnsCredited || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={12}>
                    <Card title="Reject Reasons" size="small">
                      {(grn.rejectReasons?.length || 0) === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40 }}><Text type="secondary">No rejections recorded</Text></div>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={grn.rejectReasons || []} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="reason" width={120} tick={{ fontSize: 11 }} />
                            <RechartsTooltip />
                            <Bar dataKey="qty" fill="#ff4d4f" name="Rejected Qty" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="Monthly GRN Activity" size="small">
                      {(grn.monthlyGRNTrend?.length || 0) === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40 }}><Text type="secondary">No GRN data yet</Text></div>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={grn.monthlyGRNTrend || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill="#1890ff" name="GRNs" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  </Col>
                </Row>

                {/* Vendor Quality */}
                {(grn.vendorQuality?.length || 0) > 0 && (
                  <Card title="Vendor Quality (from GRN inspections)" size="small">
                    <ResponsiveTable
                      columns={[
                        { title: 'Vendor', dataIndex: 'vendorName', width: 200 },
                        { title: 'Delivered', dataIndex: 'delivered', align: 'right' as const, width: 100 },
                        { title: 'Accepted', dataIndex: 'accepted', align: 'right' as const, width: 100, render: (v: number) => <Text type="success">{v}</Text> },
                        { title: 'Rejected', dataIndex: 'rejected', align: 'right' as const, width: 100, render: (v: number) => v > 0 ? <Text type="danger">{v}</Text> : <Text type="secondary">0</Text> },
                        { title: 'Quality Rate', dataIndex: 'qualityRate', align: 'center' as const, width: 120,
                          render: (v: number) => <Tag color={v >= 0.95 ? 'green' : v >= 0.8 ? 'orange' : 'red'}>{pct(v)}</Tag> },
                      ]}
                      dataSource={grn.vendorQuality} rowKey="vendorId" pagination={false} size="small" />
                  </Card>
                )}
              </>
            ),
          },

          // ============================== TAB 5: VENDOR PERFORMANCE ==============================
          {
            key: 'vendors',
            label: <span><RiseOutlined /> Vendors</span>,
            children: (
              <>
                <Card title="Top 5 Vendors by Impact" size="small" style={{ marginBottom: 16 }}
                  extra={<Text type="secondary">Risk-driven: fulfillment, open value, late POs</Text>}>
                  <ResponsiveTable
                    columns={[
                      { title: 'Vendor', dataIndex: 'vendorName', width: 180 },
                      { title: 'PO Value', dataIndex: 'poValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Received', dataIndex: 'receivedValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Fulfill %', dataIndex: 'fulfillment', align: 'right' as const, width: 85, render: (v: number) => v > 0 ? pct(v) : '-' },
                      { title: 'Open Value', dataIndex: 'openValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Late POs', dataIndex: 'latePOs', align: 'right' as const, width: 75 },
                      { title: 'Risk', dataIndex: 'risk', width: 80, render: (v: string) => <Tag color={v === 'High' ? 'error' : v === 'Medium' ? 'warning' : v === 'Low' ? 'success' : 'default'}>{v}</Tag> },
                      { title: 'Action', dataIndex: 'action', width: 130 },
                    ]}
                    dataSource={data?.top5Vendors || []} rowKey="vendorId" pagination={false} size="small" />
                </Card>

                <Card title="All Vendors" size="small">
                  <ResponsiveTable
                    columns={[
                      { title: 'Vendor', dataIndex: 'vendorName', width: 180 },
                      { title: 'PO Value', dataIndex: 'poValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Received', dataIndex: 'receivedValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Fulfill %', dataIndex: 'fulfillment', align: 'right' as const, width: 85, render: (v: number) => v > 0 ? pct(v) : '-' },
                      { title: 'Open Value', dataIndex: 'openValue', align: 'right' as const, width: 110, render: (v: number) => fmt(v) },
                      { title: 'Late POs', dataIndex: 'latePOs', align: 'right' as const, width: 75 },
                      { title: 'Risk', dataIndex: 'risk', width: 80, render: (v: string) => <Tag color={v === 'High' ? 'error' : v === 'Medium' ? 'warning' : v === 'Low' ? 'success' : 'default'}>{v}</Tag> },
                      { title: 'Impact', dataIndex: 'impactScore', align: 'right' as const, width: 100, render: (v: number) => v.toLocaleString() },
                    ]}
                    dataSource={data?.vendorSpend || []} rowKey="vendorId" pagination={{ pageSize: 15 }} size="small" scroll={{ x: 1100 }} />
                </Card>
              </>
            ),
          },

          // ============================== TAB 6: DEPARTMENT SOP ==============================
          {
            key: 'dept-sop',
            label: <span><SafetyOutlined /> Dept SOP</span>,
            children: (
              <>
                <Card title="Department Behavior SOP - Lifecycle KPIs" size="small" style={{ marginBottom: 16 }}
                  extra={<Text type="secondary">RAG: Approval &ge;90%, Rejection &le;5%, A&rarr;PO &le;2d</Text>}>
                  <ResponsiveTable
                    columns={[
                      { title: 'Dept', dataIndex: 'departmentName', fixed: 'left' as const, width: 130,
                        render: (v: string, r: any) => <Space><Badge color={RAG_COLORS[r.rag] || '#d9d9d9'} /><span style={{ fontWeight: 500 }}>{v}</span></Space> },
                      { title: 'MRs', dataIndex: 'prSubmittedCount', width: 60, align: 'right' as const },
                      { title: 'MR Value', dataIndex: 'prSubmittedValue', width: 100, align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'Approval %', dataIndex: 'approvalRate', width: 85, align: 'right' as const,
                        render: (v: number, r: any) => <span style={{ color: v < 0.8 ? '#ff4d4f' : v < 0.9 ? '#faad14' : '#52c41a', fontWeight: 600 }}>{r.rag === 'No Data' ? '-' : pct(v)}</span> },
                      { title: 'Reject %', dataIndex: 'rejectionRate', width: 75, align: 'right' as const,
                        render: (v: number, r: any) => <span style={{ color: v > 0.1 ? '#ff4d4f' : v > 0.05 ? '#faad14' : '#52c41a', fontWeight: 600 }}>{r.rag === 'No Data' ? '-' : pct(v)}</span> },
                      { title: 'Wait PO', dataIndex: 'approvedLinesWaitingPO', width: 70, align: 'right' as const,
                        render: (v: number) => <span style={{ color: v > 5 ? '#ff4d4f' : v > 0 ? '#faad14' : undefined }}>{v}</span> },
                      { title: 'PO Conv %', dataIndex: 'poConversion', width: 80, align: 'right' as const, render: (v: number, r: any) => r.rag === 'No Data' ? '-' : pct(v) },
                      { title: 'A&rarr;PO', dataIndex: 'avgApprovedToPODays', width: 70, align: 'right' as const, render: (v: number) => v > 0 ? `${v.toFixed(1)}d` : '-' },
                      { title: 'Fulfill %', dataIndex: 'receivingFulfillment', width: 75, align: 'right' as const, render: (v: number) => v > 0 ? pct(v) : '-' },
                      { title: 'Open Val', dataIndex: 'openValue', width: 100, align: 'right' as const, render: (v: number) => fmt(v) },
                      { title: 'RAG', dataIndex: 'rag', width: 70, render: (v: string) => <Tag color={v === 'Green' ? 'success' : v === 'Amber' ? 'warning' : v === 'Red' ? 'error' : 'default'}>{v}</Tag> },
                      { title: 'Action', dataIndex: 'action', width: 80 },
                    ]}
                    dataSource={data?.departmentKPIs || []} rowKey="departmentId" pagination={false} size="small" scroll={{ x: 1200 }}
                    rowClassName={(r: any) => r.rag === 'Red' ? 'row-red' : r.rag === 'Amber' ? 'row-amber' : ''} />
                </Card>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card title="Top 5 Depts to Escalate" size="small">
                      <ResponsiveTable
                        columns={[
                          { title: '#', key: 'rank', width: 40, render: (_: any, __: any, i: number) => i + 1 },
                          { title: 'Department', dataIndex: 'departmentName' },
                          { title: 'Score', dataIndex: 'exceptionScore', align: 'right' as const },
                          { title: 'Escalation', dataIndex: 'escalationAction', render: (v: string) => v !== 'Maintain' ? <Text type="danger">{v}</Text> : v },
                          { title: 'Owner/SLA', dataIndex: 'ownerSLA' },
                        ]}
                        dataSource={data?.top5DeptEscalate || []} rowKey="departmentName" pagination={false} size="small" card={{ pickers: ['rank'], header: ['departmentName'] }} />
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="Top 3 Delay / Rejection" size="small">
                      <ResponsiveTable
                        columns={[
                          { title: '#', key: 'rank', width: 40, render: (_: any, __: any, i: number) => i + 1 },
                          { title: 'Department', dataIndex: 'departmentName' },
                          { title: 'Rejection %', dataIndex: 'rejectionPct', align: 'right' as const, render: (v: number) => pct(v) },
                          { title: 'Avg MR&rarr;PO', dataIndex: 'avgMRToPO', align: 'right' as const, render: (v: number) => v > 0 ? `${v.toFixed(1)}d` : '-' },
                        ]}
                        dataSource={data?.top3DeptDelayRej || []} rowKey="departmentName" pagination={false} size="small" card={{ pickers: ['rank'], header: ['departmentName'] }} />
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

        ]} />
      </Spin>

      <style>{`
        .row-red { background-color: #fff2f0 !important; }
        .row-red:hover > td { background-color: #ffccc7 !important; }
        .row-amber { background-color: #fffbe6 !important; }
        .row-amber:hover > td { background-color: #fff1b8 !important; }
      `}</style>
    </div>
  );
};

export default Dashboard;
